import React, { PureComponent } from 'react';
import ReactTooltip from 'react-tooltip';
import { Option } from 'react-select';

/**
 * A higher order component (HOC) for returning a 'react-select' Option component decorated
 * with `react-tooltip` props for displaying a tooltip on MouseEnter.
 *
 * This HOC is automatically used by the ChartsSelect component when the tooltip prop is set to
 * true.
 *
 * @param {String} tooltipId         - the id for the ReactTooltip.
 *                                     This is automatically generated by the ChartsSelect component.
 * @param {Func} OptionComponent     - the component to render as a `react-select` Option.
 *                                     Defaults to the standard `react-select` Option component.
 * @returns {Func} OptionWithTooltip - the decorated component
 **/
const optionWithToolTipHoc = (tooltipId, OptionComponent = Option) => {
  class OptionWithTooltip extends PureComponent {
    static displayName = 'OptionWithTooltip';

    static propTypes = {
      ...OptionComponent.propTypes,
    };

    static defaultProps = {
      ...OptionWithTooltip.defaultProps,
    };

    constructor(props) {
      super(props);

      this.handleFocus = this.handleFocus.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleSelect = this.handleSelect.bind(this);
    }

    /**
     * Triggered when the OptionComponent has focus.
     * Show the ReactTooltip
     *
     * @param {Object} event - the MouseEnter/MouseMove event
     **/
    handleFocus(event) {
      this.props.onFocus(this.props.option, event);
      ReactTooltip.show(this._tooltip);
    }

    /**
     * Triggered when the MouseLeave event is fired
     * Hide the ReactTooltip
     **/
    handleMouseLeave() {
      ReactTooltip.hide(this._tooltip);
    }

    /**
     * Triggered when the MouseDown event is fired on the OptionComponent
     * Hides the tooltip and calls this.props.onSelect
     *
     * @param {Object} event - the MouseDown event
     */
    handleSelect(event) {
      this.props.onSelect(this.props.option, event);
      ReactTooltip.hide(this._tooltip);
    }

    render() {
      return (
        <div
          ref={ref => {
            this._tooltip = ref;
          }}
          data-for={tooltipId}
          data-tip={this.props.option.tooltip}
          onMouseOver={this.handleFocus}
          onMouseLeave={this.handleMouseLeave}
        >
          <OptionComponent
            {...this.props}
            onSelect={this.handleSelect}
            onFocus={this.handleFocus}
          />
        </div>
      );
    }
  }

  return OptionWithTooltip;
};

export default optionWithToolTipHoc;
export { optionWithToolTipHoc };
